import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import { Modal, Typography, Rate, Input, Button, Flex, message } from 'antd';
import { CustomButton } from '@/components/ui';
import '../../../styles/review-modal.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Props {
  open: boolean;
  onClose: () => void;
  onSkip: () => void;
  onSubmit: (payload: any) => Promise<void>;
  franchisorName: string;
  bookingTime: string | Date;
  bookingId: string;
}

export const ConsultantFeedbackModal: FC<Props> = ({
  open,
  onClose,
  onSkip,
  onSubmit,
  franchisorName,
  bookingId,
  bookingTime,
}) => {
  // if (!bookingId || !franchisorName) return false;
  const [loading, setLoading] = useState(false);

  const [ratings, setRatings] = useState({
    opportunityStrength: 0,
    repEffectiveness: 0,
    recommendationLikelihood: 0,
  });

  const [additionalFeedback, setAdditionalFeedback] = useState('');

  const isValid =
    ratings.opportunityStrength > 0 &&
    ratings.repEffectiveness > 0 &&
    ratings.recommendationLikelihood > 0;

  const formattedDate = dayjs(bookingTime).format('MMMM D, YYYY, [at] h:mm A');

  const handleSubmit = async () => {
    if (!isValid) {
      message.error('Please complete all required ratings.');
      return;
    }

    try {
      setLoading(true);

      await onSubmit({
        bookingId,
        opportunityStrength: ratings.opportunityStrength,
        repEffectiveness: ratings.repEffectiveness,
        recommendationLikelihood: ratings.recommendationLikelihood,
        additionalFeedback,
      });

      message.success('Feedback submitted successfully.');
      closeModal();
    } catch (error: any) {
      message.error(error?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  function closeModal() {
    onClose();
    setRatings({
      opportunityStrength: 0,
      repEffectiveness: 0,
      recommendationLikelihood: 0,
    });
    setAdditionalFeedback('');
  }

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={closeModal}
      maskClosable={false}
      keyboard={false}
      centered
      width={700}
      destroyOnClose
      styles={{
        body: {
          padding: '10px 30px',
        },
      }}
    >
      <Flex vertical gap={20}>
        {/* Header */}
        <Title level={4}>Rate Meeting with {franchisorName}</Title>

        <Text type="secondary" style={{ marginTop: '-10px' }}>
          {`You’ve had a meeting with ${franchisorName} on ${formattedDate}. Please share your
          feedback to help enhance the experience for consultants like you and
          ensure the highest-quality franchisor connections.`}
        </Text>

        <Text italic type="secondary">
          *This feedback is confidential and will not be shared directly with
          the franchisor or attributed to you.
        </Text>
      </Flex>

      <Flex vertical className={'header'} gap={18} style={{ marginTop: 20 }}>
        {/* Question 1 */}
        <Flex vertical gap={14}>
          <Text style={{ color: '#49586F', fontWeight: 600 }}>
            How strong do you feel the franchise opportunity is?
          </Text>
          <Rate
            className="blue-rate"
            value={ratings.opportunityStrength}
            onChange={(value) =>
              setRatings((prev) => ({
                ...prev,
                opportunityStrength: value,
              }))
            }
          />
        </Flex>

        {/* Question 2 */}
        <Flex vertical gap={14}>
          <Text strong style={{ color: '#49586F', fontWeight: 600 }}>
            How effective/capable was the franchise development rep you spoke
            with?
          </Text>
          <Rate
            className="blue-rate"
            value={ratings.repEffectiveness}
            onChange={(value) =>
              setRatings((prev) => ({
                ...prev,
                repEffectiveness: value,
              }))
            }
          />
        </Flex>

        {/* Question 3 */}
        <Flex vertical gap={14}>
          <Text
            style={{
              color: '#49586F',
              fontStyle: 'semi-bold',
              fontWeight: 600,
            }}
          >
            How likely are you to recommend this franchise to one of your
            candidates?
          </Text>
          <Rate
            className="blue-rate"
            value={ratings.recommendationLikelihood}
            onChange={(value) =>
              setRatings((prev) => ({
                ...prev,
                recommendationLikelihood: value,
              }))
            }
          />
        </Flex>

        {/* Additional Feedback */}
        <Flex vertical gap={8} style={{ marginTop: 20 }}>
          <TextArea
            className="consultant-feedback-textarea"
            rows={5}
            placeholder="Please provide a few sentences of additional feedback about the company, opportunity, the presentation etc."
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
          />
        </Flex>

        {/* Buttons */}
        <Flex vertical gap={12}>
          <CustomButton
            type={'primary'}
            text={'SUBMIT'}
            disabled={loading}
            onClick={handleSubmit}
          />

          <CustomButton
            type={'default'}
            onClick={onSkip}
            // text={isAdminView ? 'CLOSE' : 'SKIP'}
            text={'SKIP'}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
